import { ensureToken, fetchJsonWithAuth } from './authApi';
import { AUTH_URL } from './apiConfig';

// ── Common Module Documents API ───────────────────────────────────────────────
// Module-agnostic document registry (Auth gateway "documents" collection, files
// in S3). Every module's Add Form registers its uploads here AFTER the module
// record is created — the module's Create API generates the reference id
// (jobId / candidateId / submissionId), and that id is what links each document
// row back to its record.

// Strips the transport decorations collectFileParts may leave on a part key —
// the moduleWrites routing prefix ("__mw__<module>__") and an addRow row-index
// suffix ("[0]") — so the registered fieldName is the clean form-field key
// (e.g. "resume", "jobDescription", "offerLetter").
function cleanFieldName(formKey) {
  return String(formKey ?? '')
    .replace(/^__mw__.*?__/, '')
    .replace(/\[\d+\]$/, '')
    .trim() || 'file';
}

/**
 * uploadModuleDocuments — registers uploaded files against a created module
 * record. Common across ALL modules: pass the module name and the record id
 * its Create API returned.
 *
 * @param moduleName the module key (e.g. "jobs", "candidates", "submissions")
 * @param refId      the created record's _id (jobId / candidateId / …)
 * @param fileParts  [{ formKey, file }] — the shape collectFileParts returns;
 *                   formKey is the form field the file was uploaded against
 * @param metadata   optional { [fieldName]: {...} } extra metadata per field
 */
export async function uploadModuleDocuments(moduleName, refId, fileParts = [], metadata = {}) {
  if (!moduleName) throw new Error('moduleName is required to upload documents');
  if (!refId) throw new Error('refId (created record id) is required to upload documents');
  if (!fileParts.length) return { data: [], total: 0 };

  const token = await ensureToken();

  // NOTE: do NOT set Content-Type — the browser must add the multipart
  // boundary itself (same convention as createModuleRecord).
  const formData = new FormData();
  fileParts.forEach(({ formKey, file }) => {
    const fileName = file?.name ?? undefined;
    formData.append(cleanFieldName(formKey), file, fileName);
  });
  if (metadata && Object.keys(metadata).length > 0) {
    formData.append('metadata', JSON.stringify(metadata));
  }

  const params = new URLSearchParams({ module: moduleName, refId: String(refId) });
  const res = await fetch(`${AUTH_URL}/documents/upload?${params.toString()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const error = new Error(data?.error || data?.message || `API ${res.status}: ${res.statusText}`);
    error.status = res.status;
    error.data = data;
    throw error;
  }
  return data?.data ?? data;
}

/**
 * getModuleDocuments — lists a module record's documents (newest first), each
 * carrying a 24h presigned S3 download URL as `fileUrl`.
 *
 * @param moduleName the module key (e.g. "jobs", "candidates", "submissions")
 * @param refId      the module record's _id
 * @param fieldName  optional — only documents uploaded against this form field
 */
export async function getModuleDocuments(moduleName, refId, fieldName = '') {
  if (!moduleName) throw new Error('moduleName is required to fetch documents');
  if (!refId) throw new Error('refId is required to fetch documents');
  const params = new URLSearchParams({ module: moduleName, refId: String(refId) });
  if (fieldName) params.set('fieldName', fieldName);
  const json = await fetchJsonWithAuth(AUTH_URL, `/documents?${params.toString()}`);
  const payload = json?.data ?? json ?? {};
  return {
    data: Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []),
    total: payload?.total ?? 0,
  };
}
